import { useQueryClient } from 'react-query';
import { useMutation } from '@frontend/react-query-helpers';
import { deleteInvoice } from './api';
import { cancelInvoiceQueryCache, updateInvoiceQueryCache } from './invoice-cache-helpers';
import { getInvoiceQueryKey } from './query-keys';
import { InvoiceModel, InvoiceStatus } from './types';

export type UseCancelInvoiceMutationProps = {
  locationId: string;
  onSuccess?: (invoice: InvoiceModel) => void;
  onError?: (error: any, invoice: InvoiceModel) => void;
};
export const useCancelInvoiceMutation = ({ onSuccess, onError }: UseCancelInvoiceMutationProps) => {
  const queryClient = useQueryClient();

  const { mutateAsync, status, reset } = useMutation(async (invoice: InvoiceModel) => deleteInvoice(invoice), {
    onMutate: async (invoice: InvoiceModel) => {
      queryClient.cancelQueries(getInvoiceQueryKey(invoice.id));

      const newInvoice: InvoiceModel = {
        ...invoice,
        status: InvoiceStatus.Canceled,
      };

      cancelInvoiceQueryCache(queryClient, newInvoice, getInvoiceQueryKey(newInvoice.id));

      /**
       * Rollback function
       */
      return () => updateInvoiceQueryCache(queryClient, invoice, getInvoiceQueryKey(invoice.id));
    },
    onSuccess: (_, invoice) => {
      onSuccess?.(invoice);
    },
    onError: (error, invoice, rollback) => {
      rollback?.();
      onError?.(error, invoice);
    },
  });

  return {
    cancelInvoice: mutateAsync,
    status: status,
    reset: reset,
  };
};
